import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';

import { Button } from 'components/ui/button';
import { getTitleComment } from 'lib/utils';
import useAuthStore from 'store/auth';
import { QuestionnaireItem } from 'types';
import { PATH_ROUTES } from 'routes/path';
import { postHealthRisksQuestions } from 'services/risk-checker/api';
import { Loader2 } from 'lucide-react';
import { useExecuteRiskEngine } from 'services/risk-checker/hooks';
import useRiskCheckerStore from 'store/risk-checker';
import { useQuestionnaireStore } from 'store/questionnaire';

export default function RiskScanConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const question = location.state as QuestionnaireItem;

  const locale = useAuthStore((store) => store.locale);
  const engineName = useRiskCheckerStore((store) => store.engineName);
  const setQuestionData = useQuestionnaireStore(
    (store) => store.setQuestionData
  );

  const { mutateAsync: executeRiskEngineMutation } = useExecuteRiskEngine();

  const [isLoading, setIsLoading] = useState<string>('');

  const langQuestion =
    locale === 'es' ? question?.spanish_question : question?.eng_question;
  const { title, comment } = getTitleComment(langQuestion || '');

  const langChoices =
    locale === 'es' ? question?.spanish_choices : question?.eng_choices;

  const onSubmit = async (
    index: number,
    engChoice: string,
    spanishChoice: string
  ) => {
    if (engChoice === 'Exit') {
      return navigate(PATH_ROUTES.RISK_ANALYSIS);
    }

    if (engChoice === 'Initiate Scan') {
      navigate(
        PATH_ROUTES.START_FACE_SCAN + `?redirectTo=${PATH_ROUTES.RISK_ANALYSIS}`
      );
      return;
    }

    setIsLoading(String(index));
    try {
      const payloadData = {
        choice_value: engChoice,
        spanish_choice_value: spanishChoice,
        question_sequence: question?.question_sequence || 1,
        answer_id: question?.answer_id || null,
        question_id: question?.q_id || '',
        isEdit: false,
      };

      const res = await postHealthRisksQuestions({
        data: payloadData,
        scanFlag: true,
      });
      console.log('🚀 ~ onSubmit ~ res:', res);

      if (engChoice.includes('Initiate Scan')) {
        navigate(
          PATH_ROUTES.START_FACE_SCAN +
            `?redirectTo=${PATH_ROUTES.RISK_ANALYSIS}`
        );
        return;
      }

      if (res?.screen_name === 'generate_risk_score') {
        await executeRiskEngineMutation({ risk_type: engineName! });
        navigate(PATH_ROUTES.RISK_ANALYSIS_DETAIL);
      }

      if (res?.screen_name === 'questionnaire') {
        setQuestionData(res.data);
        navigate(PATH_ROUTES.RISK_ANALYSIS_QUESTIONNAIRE);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    } finally {
      setIsLoading('');
    }
  };

  return (
    <div className='flex items-center justify-center  lg:h-[calc(100vh-8rem)] overflow-hidden'>
      <div className='max-w-md mx-auto space-y-4 text-center'>
        <div>
          <h2 className='mb-2 text-lg font-medium leading-tight'>{title}</h2>
          <p className='text-sm leading-tight'>{comment}</p>
        </div>

        <div className='flex flex-col sm:flex-row items-center justify-center gap-4 !mt-8'>
          {JSON.parse(langChoices || '[]')?.map(
            (choice: string, index: number) => {
              if (question.path_type === 'no_scan_data' && choice === 'Exit')
                return null;

              return (
                <Button
                  key={choice}
                  variant='outline'
                  className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
                  onClick={() => {
                    const engChoices = JSON.parse(
                      question?.eng_choices || '[]'
                    );
                    const spanishChoices = JSON.parse(
                      question.spanish_choices || '[]'
                    );
                    onSubmit(index, engChoices[index], spanishChoices[index]);
                  }}
                  disabled={!!isLoading}
                >
                  {isLoading === String(index) && (
                    <Loader2 className='mr-2 animate-spin size-4' />
                  )}
                  {choice}
                </Button>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
}
